import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import InputForm from "./pages/InputForm";
import rulesPack from "./data/rules.json";
import { CalculatorInputs, RulesPack, CalculationOutput, RetentionType, VATType } from "./types";
import { calculateAll } from "./utils/calculator";
import ErrorBanner from "./components/ErrorBanner";
import SensitivitySliders from "./components/SensitivitySliders";
import Shell from "./components/ui/Shell";
import Accordion from "./components/ui/Accordion";
import Header from "./components/Header";
import Hero from "./components/Hero";
import FinancialSummary from "./components/FinancialSummary";
import FiscalDetails, { QuarterlyTab } from "./components/FiscalDetails";
import Footer from "./components/Footer";
import SaveSimulationDialog from "./components/SaveSimulationDialog";
import SimulationsModal from "./components/SimulationsModal";
import CalculationBreakdown from "./components/CalculationBreakdown";
import InverseCalculatorModal from "./components/InverseCalculatorModal";
import { loadSimulations, SimulationRecord } from "./lib/simulationsStore";
import { PeriodMode } from "./utils/period";
import { useAuth } from "./contexts/AuthContext";
import PaywallModal from "./components/PaywallModal";
import HelpGlossary from "./components/HelpGlossary";
import FloatingHelpButton from "./components/FloatingHelpButton";
import ExamplesSection from "./components/ExamplesSection";
import PremiumBanner from "./components/PremiumBanner";
import SEOHead from "./components/SEOHead";
import StructuredData from "./components/StructuredData";
import { seoConfig } from "./utils/seo";
import { generateWebSiteSchema, generateSoftwareApplicationSchema, generateOrganizationSchema, generateFAQSchema } from "./utils/structuredData";
import { captureEvent } from "./lib/analytics";
import MobileNavTabs from "./components/MobileNavTabs";
import MobileKpiStrip from "./components/MobileKpiStrip";
import PeriodToggle from "./components/PeriodToggle";
import Results from "./pages/Results";

const STORAGE_KEY_A = "calc-autonomos-v1A";
const STORAGE_KEY_B = "calc-autonomos-v1B";
const STORAGE_HIDE_HERO = "hide-hero-banner";

type MobileSection = "datos" | "resumen" | "detalle" | "trimestres";
type FiscalTab = "irpf" | "iva" | "quarterly" | "compare";

const defaultInputs: CalculatorInputs = {
  year: 2026,
  region: "general",
  regime: "estimacion_directa_simplificada",
  hasROI: false,
  activities: [],
  costs: [],
  amortizations: [],
  retenApplies7Pct: false,
  personal: {
    maritalStatus: "single",
    children: [],
    disabilityPct: 0,
    familyNumerous: "none",
    age: 45,
    rentHouseholder: false,
    pensionContribAnnual: 0,
    donationsAnnual: 0,
    healthInsuranceAnnual: 0,
    healthInsuranceCoveredPersons: 0
  },
  profile: {
    retaActiveMode: "full_year",
    retaActiveDays: undefined
  },
  coworkers: false,
  housingAffectationPct: 0,
  utilitiesAnnual: 0,
  vehicleAffectationPct: 0,
  vehicleCostsAnnual: 0,
  socialSecurityAnnual: 0,
  cashTiming: { collectionLagDays: 30, payCostsLagDays: 0 },
  aplicaDFJ: false,
  autoRETA: true
};

function applySensitivity(i: CalculatorInputs, incomeMul: number, costMul: number): CalculatorInputs {
  const clone: CalculatorInputs = JSON.parse(JSON.stringify(i));
  clone.activities = clone.activities.map(a => ({ ...a, baseAmount: Math.max(0, a.baseAmount * incomeMul) }));
  clone.costs = clone.costs.map(c => ({ ...c, baseAmount: Math.max(0, c.baseAmount * costMul) }));
  return clone;
}

function normalizeInputs(raw: Partial<CalculatorInputs>): CalculatorInputs {
  return {
    ...defaultInputs,
    ...raw,
    activities: raw.activities ?? defaultInputs.activities,
    costs: raw.costs ?? defaultInputs.costs,
    amortizations: raw.amortizations ?? defaultInputs.amortizations,
    personal: { ...defaultInputs.personal, ...(raw.personal ?? {}) },
    profile: { ...defaultInputs.profile, ...(raw.profile ?? {}) },
    cashTiming: { ...defaultInputs.cashTiming, ...(raw.cashTiming ?? {}) }
  };
}

function parseInputs(raw: string | null): CalculatorInputs {
  if (!raw) return defaultInputs;
  try {
    return normalizeInputs(JSON.parse(raw));
  } catch {
    return defaultInputs;
  }
}

export default function App() {
  const { user, loading: authLoading } = useAuth();
  const [active, setActive] = useState<"A" | "B">("A");
  const [inputsA, setInputsA] = useState<CalculatorInputs>(() => {
    return parseInputs(localStorage.getItem(STORAGE_KEY_A));
  });
  const [inputsB, setInputsB] = useState<CalculatorInputs>(() => {
    return parseInputs(localStorage.getItem(STORAGE_KEY_B));
  });

  const [incomeMulA, setIncomeMulA] = useState(1);
  const [costMulA, setCostMulA] = useState(1);
  const [incomeMulB, setIncomeMulB] = useState(1);
  const [costMulB, setCostMulB] = useState(1);

  const [outputA, setOutputA] = useState<CalculationOutput | null>(null);
  const [outputB, setOutputB] = useState<CalculationOutput | null>(null);
  const [baselineA, setBaselineA] = useState<CalculationOutput | null>(null);
  const [baselineB, setBaselineB] = useState<CalculationOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEffective, setShowEffective] = useState(true);
  const [periodMode, setPeriodMode] = useState<PeriodMode>("annual");

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [showSimModal, setShowSimModal] = useState(false);
  const [inverseOpen, setInverseOpen] = useState(false);
  const [simulations, setSimulations] = useState<SimulationRecord[]>([]);
  const [currentSimulationId, setCurrentSimulationId] = useState<string | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState<string>('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [editedA, setEditedA] = useState(false);
  const [editedB, setEditedB] = useState(false);
  const [exampleLoadedA, setExampleLoadedA] = useState(false);
  const [exampleLoadedB, setExampleLoadedB] = useState(false);
  const [hideHeroBanner, setHideHeroBanner] = useState<boolean>(() => {
    return localStorage.getItem(STORAGE_HIDE_HERO) === "1";
  });
  const [isMobile, setIsMobile] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState<MobileSection>("datos");
  const [activeFiscalTab, setActiveFiscalTab] = useState<FiscalTab>("irpf");

  const datosRef = useRef<HTMLDivElement>(null);
  const resumenRef = useRef<HTMLDivElement>(null);
  const detalleRef = useRef<HTMLDivElement>(null);
  const trimestresRef = useRef<HTMLDivElement>(null);

  const isPremium = !!user;
  const [headerHeight, setHeaderHeight] = useState(80);
  const showHeroBanner = !isPremium && !hideHeroBanner;
  const homeFaqs = [
    {
      question: "¿Qué calcula Autónomo Claro?",
      answer:
        "Calcula el IRPF, la cuota de autónomos (RETA), el IVA, los anticipos trimestrales y el neto final estimado para autónomos en España.",
    },
    {
      question: "¿La calculadora sirve para 2025 y 2026?",
      answer:
        "Sí. La home y la simulación están orientadas a normativa 2025/2026, incluyendo IRPF, RETA e IVA con vista anual y detalle de renta.",
    },
    {
      question: "¿Puedo estimar cuánto me quedará después de la renta?",
      answer:
        "Sí. La app muestra caja previa, IRPF total, anticipos, regularización de la renta y neto final estimado.",
    },
    {
      question: "¿Puedo guardar simulaciones?",
      answer:
        "Sí. Con alta Premium puedes guardar simulaciones, comparar escenarios A/B y exportar informes en PDF o CSV.",
    },
  ];

  const rules = rulesPack as RulesPack;


  useEffect(() => {
    loadSimulations(user).then(setSimulations);
  }, [user]);

  useEffect(() => {
    localStorage.setItem(STORAGE_HIDE_HERO, hideHeroBanner ? "1" : "0");
  }, [hideHeroBanner]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 960px)");
    const handler = (e: MediaQueryList | MediaQueryListEvent) => {
      setIsMobile("matches" in e ? e.matches : (e as MediaQueryList).matches);
    };
    handler(mq);
    const listener = (event: MediaQueryListEvent) => handler(event);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);

  useEffect(() => {
    if (outputA || outputB) {
      captureEvent('calculation_completed', {
        scenario: active,
        hasOutputA: !!outputA,
        hasOutputB: !!outputB,
      });
    }
  }, [outputA, outputB]);

  useLayoutEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;

    const updateHeight = () => {
      const nextHeight = Math.ceil(header.getBoundingClientRect().height);
      if (nextHeight && nextHeight !== headerHeight) {
        setHeaderHeight(nextHeight);
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [headerHeight]);

  useEffect(() => { localStorage.setItem(STORAGE_KEY_A, JSON.stringify(inputsA)); }, [inputsA]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_B, JSON.stringify(inputsB)); }, [inputsB]);

  const effectiveA = useMemo(() => applySensitivity(inputsA, incomeMulA, costMulA), [inputsA, incomeMulA, costMulA]);
  const effectiveB = useMemo(() => applySensitivity(inputsB, incomeMulB, costMulB), [inputsB, incomeMulB, costMulB]);

  // Baseline se calcula solo cuando cambian los inputs base, no cuando cambian los sliders
  useEffect(() => {
    try {
      const baseA = calculateAll(inputsA, rules);
      const baseB = calculateAll(inputsB, rules);
      setBaselineA(baseA);
      setBaselineB(baseB);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "Error de cálculo");
      setBaselineA(null);
      setBaselineB(null);
    }
  }, [inputsA, inputsB, rules]);

  // Output se recalcula cuando cambian los inputs efectivos (con sliders aplicados)
  useEffect(() => {
    try {
      const a = calculateAll(effectiveA, rules);
      const b = calculateAll(effectiveB, rules);
      setOutputA(a);
      setOutputB(b);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "Error de cálculo");
      setOutputA(null);
      setOutputB(null);
    }
  }, [effectiveA, effectiveB, rules]);

  const activeInputs = active === "A" ? inputsA : inputsB;
  const setActiveInputs = active === "A" ? setInputsA : setInputsB;
  const setActiveEdited = active === "A" ? setEditedA : setEditedB;
  const activeOutput = active === "A" ? outputA : outputB;
  const activeEdited = active === "A" ? editedA : editedB;
  const activeExampleLoaded = active === "A" ? exampleLoadedA : exampleLoadedB;
  const markEdited = () => setActiveEdited(true);
  const exampleData: CalculatorInputs = {
    ...defaultInputs,
    region: "cataluna",
    activities: [
      { concept: "Consultoría", baseAmount: 30000, vatType: "general_21", retenType: "profesional_15" },
      { concept: "Docencia", baseAmount: 5000, vatType: "exempt", retenType: "none", exempt: true }
    ],
    costs: [
      { concept: "Coworking", baseAmount: 3000, hasVAT: true, vatType: "general_21", deductiblePct: 100 },
      { concept: "Software", baseAmount: 600, hasVAT: true, vatType: "general_21", deductiblePct: 100 }
    ],
    housingAffectationPct: 20,
    utilitiesAnnual: 1200,
    vehicleAffectationPct: 50,
    vehicleCostsAnnual: 1000,
    socialSecurityAnnual: 3000,
    personal: { ...defaultInputs.personal, healthInsuranceAnnual: 500, healthInsuranceCoveredPersons: 1 }
  };

  const loadExample = () => {
    captureEvent('load_example', { scenario: active });
    if (active === "A") {
      setInputsA(exampleData);
      setEditedA(true);
      setExampleLoadedA(true);
    } else {
      setInputsB(exampleData);
      setEditedB(true);
      setExampleLoadedB(true);
    }
  };

  const resetCurrent = () => {
    captureEvent('reset_scenario', { scenario: active });
    if (active === "A") {
      setInputsA(defaultInputs);
      setEditedA(true);
      setExampleLoadedA(false);
    } else {
      setInputsB(defaultInputs);
      setEditedB(true);
      setExampleLoadedB(false);
    }
    setCurrentSimulationId(null);
  };

  const duplicateScenario = () => {
    if (!isPremium) {
      setPaywallFeature('scenarios');
      setPaywallOpen(true);
      return;
    }

    captureEvent('duplicate_scenario', { from: active, to: active === "A" ? "B" : "A" });
    if (active === "A") {
      setInputsB(inputsA);
      setActive("B");
    } else {
      setInputsA(inputsB);
      setActive("A");
    }
  };

  const scrollToSection = (ref: RefObject<HTMLElement>) => {
    if (!ref.current) return;
    const offset = headerHeight + 12;
    const top = ref.current.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleMobileNav = (section: MobileSection) => {
    setActiveMobileSection(section);
    if (section === "detalle") setActiveFiscalTab("irpf");
    if (section === "trimestres") setActiveFiscalTab("quarterly");
    const map: Record<MobileSection, RefObject<HTMLElement>> = {
      datos: datosRef,
      resumen: resumenRef,
      detalle: detalleRef,
      trimestres: trimestresRef,
    };
    scrollToSection(map[section]);
  };

  const impactAnualA = useMemo(() => {
    if (!baselineA || !outputA) return 0;
    const baseNetAnual = baselineA.netoFinalTrasRenta ?? baselineA.netoCajaMensualMedio * 12;
    const simNetAnual = outputA.netoFinalTrasRenta ?? outputA.netoCajaMensualMedio * 12;
    return simNetAnual - baseNetAnual;
  }, [baselineA, outputA]);

  const impactAnualB = useMemo(() => {
    if (!baselineB || !outputB) return 0;
    const baseNetAnual = baselineB.netoFinalTrasRenta ?? baselineB.netoCajaMensualMedio * 12;
    const simNetAnual = outputB.netoFinalTrasRenta ?? outputB.netoCajaMensualMedio * 12;
    return simNetAnual - baseNetAnual;
  }, [baselineB, outputB]);

  const impactValueA = periodMode === 'monthly' ? impactAnualA / 12 : impactAnualA;
  const impactValueB = periodMode === 'monthly' ? impactAnualB / 12 : impactAnualB;

  const baseIncomeA = useMemo(() => {
    return inputsA.activities.reduce((sum, a) => sum + (a.baseAmount || 0), 0);
  }, [inputsA.activities]);

  const baseCostA = useMemo(() => {
    return inputsA.costs.reduce((sum, c) => sum + (c.baseAmount || 0), 0);
  }, [inputsA.costs]);

  const baseIncomeB = useMemo(() => {
    return inputsB.activities.reduce((sum, a) => sum + (a.baseAmount || 0), 0);
  }, [inputsB.activities]);

  const baseCostB = useMemo(() => {
    return inputsB.costs.reduce((sum, c) => sum + (c.baseAmount || 0), 0);
  }, [inputsB.costs]);

  const activeBaseIncome = active === "A" ? baseIncomeA : baseIncomeB;
  const activeHasIncome = activeBaseIncome > 0;
  const isPristine = !activeEdited && activeBaseIncome === 0;
  const zeroIncomeTouched = activeEdited && activeBaseIncome === 0;

  const handleStart = () => {
    const next = { ...activeInputs };
    if (!next.activities || next.activities.length === 0) {
      next.activities = [{
        concept: "",
        baseAmount: 0,
        vatType: "general_21",
        retenType: "profesional_15"
      }];
    }
    setActiveInputs(next);
    setActiveEdited(true);
    setActiveMobileSection("datos");
    scrollToSection(datosRef);
  };

  const renderEmptyState = () => (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "16px",
        padding: "18px",
        background: "#fff",
        boxShadow: "var(--shadow)",
      }}
    >
      <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "6px", letterSpacing: "-0.01em" }}>
        Añade un ingreso para ver resultados
      </div>
      <div style={{ fontSize: "13px", color: "var(--muted)", lineHeight: 1.5, marginBottom: "12px" }}>
        Empieza con tu primera línea de ingresos o carga un ejemplo para ver los KPIs y el desglose fiscal.
      </div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          className="button brand"
          onClick={loadExample}
          style={{ padding: "10px 16px" }}
        >
          Cargar ejemplo
        </button>
        <button
          className="button"
          onClick={handleStart}
          style={{ padding: "10px 16px", borderRadius: "10px" }}
        >
          Empezar
        </button>
      </div>
    </div>
  );

  const handleDownloadPDF = async () => {
    if (!activeOutput) return;

    if (!isPremium) {
      setPaywallFeature('pdf');
      setPaywallOpen(true);
      return;
    }

    try {
      captureEvent('pdf_download', { scenario: active });

      const { exportPdf } = await import("./pdf/export");
      const blob = await exportPdf({ inputs: activeInputs, output: activeOutput, clientName: "Cliente" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `informe-fiscal-${active}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error al generar el PDF');
    }
  };

  const handleDownloadCSV = () => {
    if (!activeOutput) return;

    if (!isPremium) {
      setPaywallFeature('csv');
      setPaywallOpen(true);
      return;
    }

    try {
      captureEvent('csv_download', { scenario: active });

      const csvRows: string[] = [];

      csvRows.push('RESUMEN EJECUTIVO');
      csvRows.push('Concepto,Anual,Mensual');
      csvRows.push(`Bruto Facturado,${activeOutput.brutoFacturado ?? 0},${(activeOutput.brutoFacturado ?? 0) / 12}`);
      csvRows.push(`Caja Previa Renta,${activeOutput.cajaPreviaRenta ?? 0},${(activeOutput.cajaPreviaRenta ?? 0) / 12}`);
      csvRows.push(`Neto Final,${activeOutput.netoFinalTrasRenta ?? 0},${(activeOutput.netoFinalTrasRenta ?? 0) / 12}`);
      csvRows.push('');

      csvRows.push('DATOS DE CONFIGURACION');
      csvRows.push(`Año,${activeInputs.year}`);
      csvRows.push(`Región,${activeInputs.region}`);
      csvRows.push(`Situación Personal,${activeInputs.personal.maritalStatus}`);
      csvRows.push(`Hijos,${activeInputs.personal.children.length}`);
      csvRows.push(`Discapacidad (%),${activeInputs.personal.disabilityPct}`);
      csvRows.push(`Cuota de autónomos Anual,${activeInputs.socialSecurityAnnual}`);
      csvRows.push(`Cuota de autónomos Mensual,${activeInputs.socialSecurityAnnual / 12}`);
      csvRows.push('');

      if (activeInputs.activities.length > 0) {
        csvRows.push('ACTIVIDADES (INGRESOS)');
        csvRows.push('Concepto,Base Anual,Base Mensual,IVA,Retención');
        activeInputs.activities.forEach((act: any) => {
          const retention = act.retenType === 'custom' ? `${act.customRetentionPct}%` : act.retenType;
          csvRows.push(`"${act.concept}",${act.baseAmount},${act.baseAmount / 12},${act.vatType},${retention}`);
        });
        csvRows.push('');
      }

      if (activeInputs.costs.length > 0) {
        csvRows.push('GASTOS');
        csvRows.push('Concepto,Base Anual,Base Mensual,% Deducible');
        activeInputs.costs.forEach((cost: any) => {
          csvRows.push(`"${cost.concept}",${cost.baseAmount},${cost.baseAmount / 12},${cost.deductiblePct ?? 100}`);
        });
        csvRows.push('');
      }

      csvRows.push('DESGLOSE DEL CALCULO');
      csvRows.push('Concepto,Anual,Mensual');
      csvRows.push(`Rendimiento Neto,${activeOutput.rendimientoNeto ?? 0},${(activeOutput.rendimientoNeto ?? 0) / 12}`);
      csvRows.push(`Base Imponible IRPF,${activeOutput.baseImponibleIRPF ?? activeOutput.baseIRPF ?? 0},${((activeOutput.baseImponibleIRPF ?? activeOutput.baseIRPF ?? 0) / 12)}`);
      csvRows.push(`Cuota Estatal,${activeOutput.irpf?.cuotaEstatal ?? 0},${(activeOutput.irpf?.cuotaEstatal ?? 0) / 12}`);
      csvRows.push(`Cuota Autonómica,${activeOutput.irpf?.cuotaAutonomica ?? 0},${(activeOutput.irpf?.cuotaAutonomica ?? 0) / 12}`);
      csvRows.push(`Cuota Total IRPF,${activeOutput.irpf?.cuotaTotal ?? 0},${(activeOutput.irpf?.cuotaTotal ?? 0) / 12}`);
      csvRows.push(`Retenciones Practicadas,-${activeOutput.pagos?.retencionesSufridas ?? 0},-${(activeOutput.pagos?.retencionesSufridas ?? 0) / 12}`);
      csvRows.push(`Pagos Fraccionados (Mod. 130),-${activeOutput.pagos?.pagosFraccionados130 ?? 0},-${(activeOutput.pagos?.pagosFraccionados130 ?? 0) / 12}`);
      csvRows.push(`Regularización IRPF,${activeOutput.irpfRegularizacion ?? 0},${(activeOutput.irpfRegularizacion ?? 0) / 12}`);
      csvRows.push('');

      if (activeOutput.quarterly?.qrows && activeOutput.quarterly.qrows.length > 0) {
        csvRows.push('PROYECCION TRIMESTRAL');
        csvRows.push('Trimestre,Ingresos,Gastos,IVA Liquidación,Mod. 130');
        activeOutput.quarterly.qrows.forEach((q: any) => {
          csvRows.push(`Q${q.quarter},${q.ingresosImponibles},${q.gastosDeducibles},${q.liquidacionIVA},${q.pagos130}`);
        });
      }

      const csvContent = csvRows.join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `datos-fiscal-${active}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating CSV:', error);
      setError('Error al generar el CSV');
    }
  };

  const applyInverseImport = (
    choice: "scenario_b" | "replace",
    payload: {
      grossAnnual: number;
      costsAnnual: number;
      retentionType: RetentionType;
      vatType: VATType;
    }
  ) => {
    const buildInputs = (base: typeof activeInputs) => {
      const next = JSON.parse(JSON.stringify(base));
      next.activities = [
        {
          concept: "Facturación objetivo",
          baseAmount: payload.grossAnnual,
          vatType: payload.vatType,
          retenType: payload.retentionType,
          exempt: payload.vatType === "exempt",
          frequency: "annual",
        },
      ];
      next.costs =
        payload.costsAnnual > 0
          ? [
              {
                concept: "Costes estimados (inversa)",
                baseAmount: payload.costsAnnual,
                hasVAT: false,
                deductiblePct: 100,
                frequency: "annual",
              },
            ]
          : [];
      if (payload.retentionType === "profesional_7") {
        next.retenApplies7Pct = true;
      }
      return next;
    };

    if (choice === "scenario_b") {
      if (!isPremium) {
        setInputsA(activeInputs);
      }
      setInputsB(buildInputs(activeInputs));
      setActive("B");
      return;
    }

    if (active === "A") {
      setInputsA(buildInputs(activeInputs));
    } else {
      setInputsB(buildInputs(activeInputs));
    }
  };

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg)'
      }}>
        <p style={{ fontSize: '14px', color: 'var(--muted)' }}>Cargando...</p>
      </div>
    );
  }

  return (
    <>
      <SEOHead config={seoConfig.home} />
      <StructuredData id="website" data={generateWebSiteSchema()} />
      <StructuredData id="software-application" data={generateSoftwareApplicationSchema()} />
      <StructuredData id="organization" data={generateOrganizationSchema()} />
      <StructuredData id="home-faq" data={generateFAQSchema(homeFaqs)} />
      <Header
        active={active}
        showPremiumBanner={showHeroBanner}
        onDismissPremiumBanner={() => setHideHeroBanner(true)}
        onChangeActive={setActive}
        periodMode={periodMode}
        onChangePeriodMode={setPeriodMode}
        onDuplicate={duplicateScenario}
        output={activeOutput}
        showStickySummary={activeHasIncome}
        onLoadExample={loadExample}
        onReset={resetCurrent}
        onSaveSimulation={() => {
          if (!isPremium) {
            setPaywallFeature('save');
            setPaywallOpen(true);
          } else {
            setSaveDialogOpen(true);
          }
        }}
        simulationsCount={simulations.length}
        onOpenSimulations={() => setShowSimModal(true)}
        onDownloadPDF={handleDownloadPDF}
        onDownloadCSV={handleDownloadCSV}
        onOpenPremium={() => {
          setPaywallFeature("save");
          setPaywallOpen(true);
        }}
      />
      <Hero
        onOpenHelp={() => setHelpOpen(true)}
        onOpenInverse={() => setInverseOpen(true)}
        onOpenPremium={() => {
          setPaywallFeature("save");
          setPaywallOpen(true);
        }}
        onStart={handleStart}
        isPremium={isPremium}
      />
      <Shell>
        <div className="mobile-only" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <PeriodToggle value={periodMode} onChange={setPeriodMode} />
          </div>
        </div>
        {isMobile && !isPristine && (
          <>
            <MobileNavTabs
              active={activeMobileSection}
              onSelect={handleMobileNav}
              topOffset={headerHeight + 6}
            />
            {activeMobileSection === "resumen" && (
              <MobileKpiStrip output={activeOutput} periodMode={periodMode} />
            )}
          </>
        )}
        {error && <ErrorBanner message={error} />}

        <SimulationsModal
          isOpen={showSimModal}
          onClose={() => setShowSimModal(false)}
          simulations={simulations}
          onLoad={(inputs, id) => {
            if (active === "A") {
              setInputsA(inputs);
            } else {
              setInputsB(inputs);
            }
            setCurrentSimulationId(id);
            setShowSimModal(false);
          }}
          onUpdate={() => loadSimulations(user).then(setSimulations)}
        />

        <InverseCalculatorModal
          isOpen={inverseOpen}
          onClose={() => setInverseOpen(false)}
          inputs={activeInputs}
          rules={rules}
          isPremium={isPremium}
          onImport={applyInverseImport}
        />

        <div className="main-layout">
          <div className="left-column" ref={datosRef}>
            <div
              style={{
                marginBottom: "16px",
                padding: "14px 16px",
                background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)",
                borderRadius: "12px",
                border: "1px solid rgba(37, 99, 235, 0.2)",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#1e40af",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: "6px",
                }}
              >
                Tus datos
              </div>
              <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700 }}>
                Empecemos por lo esencial
              </h2>
              <p style={{ margin: 0, fontSize: "12px", color: "var(--muted)" }}>
                Ingresos, costes y situación personal
              </p>
            </div>

            <InputForm
              inputs={activeInputs}
              onChange={(next) => {
                markEdited();
                setActiveInputs(next);
              }}
              showEffective={showEffective}
              incomeMul={active === "A" ? incomeMulA : incomeMulB}
              costMul={active === "A" ? costMulA : costMulB}
              showExampleNotice={activeExampleLoaded}
              isMobile={isMobile}
            />

            {!isPristine && <Results output={activeOutput} periodMode={periodMode} />}

            {isPremium && (
              <div style={{ marginTop: "32px" }}>
                <Accordion
                  title="Simula variaciones"
                  subtitle="Sensibilidad para ver impactos sin tocar tus datos."
                  defaultOpen={false}
                >
                  {active === "A" ? (
                    <SensitivitySliders
                      incomeMultiplier={incomeMulA}
                      costMultiplier={costMulA}
                      onChangeIncome={setIncomeMulA}
                      onChangeCost={setCostMulA}
                      impactValue={impactValueA}
                      baseIncome={baseIncomeA}
                      baseCost={baseCostA}
                      periodMode={periodMode}
                    />
                  ) : (
                    <SensitivitySliders
                      incomeMultiplier={incomeMulB}
                      costMultiplier={costMulB}
                      onChangeIncome={setIncomeMulB}
                      onChangeCost={setCostMulB}
                      impactValue={impactValueB}
                      baseIncome={baseIncomeB}
                      baseCost={baseCostB}
                      periodMode={periodMode}
                    />
                  )}
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
                    <input
                      type="checkbox"
                      checked={showEffective}
                      onChange={(e) => setShowEffective(e.target.checked)}
                    />
                    Mostrar valores ajustados en el formulario
                  </label>
                </Accordion>
              </div>
            )}
          </div>

          <div className="right-column" ref={resumenRef}>
            {isPristine ? (
              renderEmptyState()
            ) : (
              <>
                {zeroIncomeTouched && (
                  <div
                    style={{
                      marginBottom: "12px",
                      padding: "12px",
                      borderRadius: "10px",
                      border: "1px solid var(--border)",
                      background: "#f8fafc",
                      color: "var(--muted)",
                      fontSize: "13px",
                    }}
                  >
                    Con 0 ingresos, la caja puede salir negativa por cuota de autónomos/costes.
                  </div>
                )}
                <FinancialSummary
                  output={activeOutput}
                  periodMode={periodMode}
                  isPremium={isPremium}
                />
              </>
            )}
          </div>
        </div>

        <div style={{ marginTop: "48px" }} ref={detalleRef}>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ margin: "0 0 4px", fontSize: "18px", fontWeight: 700 }}>
              Detalle
            </h2>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--muted)" }}>
              IRPF, IVA, trimestres y comparación
            </p>
          </div>

          {isPristine ? (
            <div style={{ marginTop: "16px" }}>
              {renderEmptyState()}
            </div>
          ) : isMobile ? (
            <>
              <Accordion title="Ver detalle" subtitle="IRPF, IVA y escenarios" defaultOpen={false}>
                <FiscalDetails
                  output={activeOutput}
                  outputA={outputA}
                  outputB={outputB}
                  periodMode={periodMode}
                  isPremium={isPremium}
                  activeTab={activeFiscalTab}
                  onTabChange={setActiveFiscalTab}
                  showQuarterlyTab={false}
                />
              </Accordion>

              <div ref={trimestresRef} style={{ marginTop: "12px" }}>
                <Accordion title="Ver trimestres" subtitle="Proyección Q1 - Q4" defaultOpen={false}>
                  <QuarterlyTab output={activeOutput} />
                </Accordion>
              </div>

              <div style={{ marginTop: "12px" }}>
                <Accordion title="Cómo se calcula" subtitle="Metodología y fórmulas" defaultOpen={false}>
                  <CalculationBreakdown
                    output={activeOutput}
                    periodMode={periodMode}
                    isPremium={isPremium}
                  />
                </Accordion>
              </div>
            </>
          ) : (
            <>
              <FiscalDetails
                output={activeOutput}
                outputA={outputA}
                outputB={outputB}
                periodMode={periodMode}
                isPremium={isPremium}
                activeTab={activeFiscalTab}
                onTabChange={setActiveFiscalTab}
              />

              <div style={{ marginTop: "32px" }}>
                <CalculationBreakdown
                  output={activeOutput}
                  periodMode={periodMode}
                  isPremium={isPremium}
                />
              </div>
            </>
          )}
        </div>

        {!isPremium && activeOutput && (
          <PremiumBanner variant="results" />
        )}
      </Shell>

      <ExamplesSection />
      <Shell>
        <section style={{ marginTop: "12px", marginBottom: "36px" }}>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ margin: "0 0 6px", fontSize: "24px", fontWeight: 700 }}>
              Preguntas frecuentes sobre la calculadora para autónomos
            </h2>
            <p style={{ margin: 0, fontSize: "14px", color: "var(--muted)", lineHeight: 1.6 }}>
              Respuestas rápidas para reforzar qué calcula esta página y para quién está pensada.
            </p>
          </div>

          <div style={{ display: "grid", gap: "10px" }}>
            {homeFaqs.map((faq) => (
              <Accordion
                key={faq.question}
                title={faq.question}
                subtitle="Respuesta breve"
                defaultOpen={false}
              >
                <div className="section-card" style={{ marginBottom: 0 }}>
                  <p style={{ margin: 0, fontSize: "14px", lineHeight: 1.7, color: "var(--text)" }}>
                    {faq.answer}
                  </p>
                </div>
              </Accordion>
            ))}
          </div>
        </section>
      </Shell>
      <Footer />

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature={paywallFeature}
      />

      {helpOpen && (
        <HelpGlossary onClose={() => setHelpOpen(false)} />
      )}

      <FloatingHelpButton onClick={() => setHelpOpen(true)} />

      {saveDialogOpen && (
        <SaveSimulationDialog
          inputs={activeInputs}
          output={activeOutput}
          currentSimulationId={currentSimulationId}
          onClose={() => setSaveDialogOpen(false)}
          onSaved={() => {
            loadSimulations(user).then(setSimulations);
            setCurrentSimulationId(null);
          }}
          onShowLogin={() => {
            setSaveDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
